import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';


const ProjectDetailContainer = styled.div`
  padding: 6rem 2rem 5rem;
  background: rgb(var(--bg));
  min-height: 100vh;
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 2rem;
  padding: 2rem;
  background: rgb(var(--fg) / 0.035);
  border: 1px solid rgb(var(--fg) / 0.06);
  border-radius: 20px;
  backdrop-filter: blur(24px) saturate(1.15);
  -webkit-backdrop-filter: blur(24px) saturate(1.15);
  box-shadow: 0 24px 56px -16px rgb(0 0 0 / calc(0.5 * var(--shadow-mul)));
  position: relative;
  overflow: hidden;

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background:
      radial-gradient(circle at 20% 20%, rgba(77, 159, 255, 0.15), transparent 45%),
      radial-gradient(circle at 82% 22%, rgba(77, 159, 255, 0.08), transparent 35%);
    pointer-events: none;
  }

  & > * {
    position: relative;
    z-index: 1;
  }
`;

const Title = styled.h1`
  font-size: 2.5rem;
  color: rgb(var(--fg) / 0.95);
  font-weight: 800;
  margin-bottom: 0.5rem;
`;

const Date = styled.p`
  font-size: 0.9rem;
  color: rgb(var(--gray) / 0.55);
`;

const TagsContainer = styled.div`
  margin-top: 1rem;
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const Tag = styled.span`
  background: rgb(var(--fg) / 0.05);
  color: rgb(var(--fg) / 0.9);
  border: 1px solid rgba(77, 159, 255, 0.3);
  padding: 0.4rem 0.8rem;
  border-radius: 999px;
  font-size: 0.8rem;
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
`;

const CoverImage = styled.img`
  display: block;
  width: 100%;
  max-width: 860px;
  height: 360px;
  object-fit: cover;
  object-position: center;
  margin: 1.5rem auto 1rem;
  border-radius: 18px;
  border: 1px solid rgb(var(--fg) / 0.08);
  box-shadow: 0 18px 36px rgb(0 0 0 / calc(0.45 * var(--shadow-mul)));
  background: rgb(var(--fg) / 0.04);
  filter: brightness(0.92) saturate(0.95);
  transition: filter 0.35s ease;

  &:hover {
    filter: brightness(1) saturate(1);
  }

  @media (max-width: 768px) {
    height: 260px;
  }

  @media (max-width: 480px) {
    height: 200px;
    max-width: 100%;
  }
`;

const Image = styled.img`
  width: 100%;
  height: auto;
  border-radius: 10px;
  margin-bottom: 2rem;
  background: rgb(var(--fg) / 0.04);
  border: 1px solid rgb(var(--fg) / 0.08);
  box-shadow: 0 12px 40px -12px rgb(0 0 0 / calc(0.6 * var(--shadow-mul)));
  filter: brightness(0.92) saturate(0.95);
`;

const Section = styled.div`
  margin-bottom: 2rem;
  padding: 2rem;
  background: rgb(var(--fg) / 0.035);
  border: 1px solid rgb(var(--fg) / 0.06);
  border-radius: 20px;
  backdrop-filter: blur(24px) saturate(1.15);
  -webkit-backdrop-filter: blur(24px) saturate(1.15);
  box-shadow: 0 24px 56px -16px rgb(0 0 0 / calc(0.5 * var(--shadow-mul)));
`;

const SectionTitle = styled.h2`
  font-size: 1.8rem;
  color: #4d9fff;
  margin-bottom: 1rem;
  border-bottom: 1px solid rgba(77, 159, 255, 0.25);
  padding-bottom: 0.5rem;
`;

const Text = styled.p`
  font-size: 1rem;
  line-height: 1.8;
  color: rgb(var(--fg) / 0.82);
  margin-bottom: 1rem;
`;

const List = styled.ul`
  padding-left: 1.5rem;
  list-style-type: disc;
  color: rgb(var(--fg) / 0.82);
`;

const ListItem = styled.li`
  margin-bottom: 0.8rem;
  line-height: 1.6;
`;

const Button = styled.button`
  display: flex;
  margin: 0 auto;
  padding: 0.8rem 2rem;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgb(var(--fg) / 0.92);
  background: rgb(var(--fg) / 0.06);
  border: 1px solid rgb(var(--fg) / 0.14);
  border-radius: 999px;
  cursor: pointer;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  transition: transform 0.25s, border-color 0.25s, background 0.25s;
  text-align: center;

  &:hover {
    transform: translateY(-1px);
    background: rgb(var(--fg) / 0.1);
    border-color: rgba(77, 159, 255, 0.5);
  }
`;

// New UI section styles
const UIImagesContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  margin-bottom: 2rem;
`;

const HalfImage = styled.img`
  width: 48%;
  height: auto;
  object-fit: contain;
  border-radius: 10px;
  margin-bottom: 2rem;
  background: rgb(var(--fg) / 0.04);
  border: 1px solid rgb(var(--fg) / 0.08);
  box-shadow: 0 12px 40px -12px rgb(0 0 0 / calc(0.6 * var(--shadow-mul)));
  filter: brightness(0.92) saturate(0.95);
`;

const CarpoolingApp = () => {
  const tags = ['React', 'Firebase', 'Full Stack', 'Mobile'];
  const datePublished = '2024-10-20';
  const navigate = useNavigate();


  return (
    <ProjectDetailContainer>
      <Header>
        <Title>Carpooling App</Title>
        <Date>Published on: {datePublished}</Date>
        <CoverImage src="/images/carpool.jpg" alt="Carpooling App" />
        <TagsContainer>
          {tags.map((tag, index) => (
            <Tag key={index}>{tag}</Tag>
          ))}
        </TagsContainer>
      </Header>

      <Section>
        <SectionTitle>Overview</SectionTitle>
        <Text>
          I began developing this project after identifying a recurring operational problem among ACG students: persistent parking shortages and inefficient commute patterns caused by overlapping schedules. Instead of approaching this purely as an app build, I used it as an opportunity to practice product scoping, data‑driven matching logic, and systems design. The prototype explored how student schedule data, geolocation clustering, and role‑based behavior (drivers vs. passengers) could form a structured decision‑support system for carpool matching. While development was paused after learning that an official university team was preparing a similar product, the work functioned as a practical exercise in analytical product thinking, architecture design, and user‑centric problem framing.
        </Text>
      </Section>

      <Section>
        <SectionTitle>Objectives</SectionTitle>
        <Text>
          The primary objectives of this project are:
        </Text>
        <List>
          <ListItem>Address the parking issue on campus by promoting carpooling among students.</ListItem>
          <ListItem>Provide a platform that matches students based on their location, schedules, and interests.</ListItem>
          <ListItem>Foster social connections and build a stronger community within the university.</ListItem>
          <ListItem>Promote sustainability through reduced vehicle usage and emissions.</ListItem>
        </List>
      </Section>

      <Section>
        <SectionTitle>Tools Used</SectionTitle>
        <Text>
          The following tools and technologies were used to develop the carpooling app:
        </Text>
        <List>
          <ListItem>React for front-end development</ListItem>
          <ListItem>Firebase for authentication and real-time database management</ListItem>
          <ListItem>Google Maps API for location services and route optimization</ListItem>
          <ListItem>Styled-components for styling the user interface</ListItem>
        </List>
      </Section>

      <Section>
        <SectionTitle>Development Process</SectionTitle>
        <Text>
          The development of the app follows an agile approach, involving the following key stages:
        </Text>
        <List>
          <ListItem>Requirement gathering from students and university administration.</ListItem>
          <ListItem>Designing the user interface to ensure an intuitive and user-friendly experience.</ListItem>
          <ListItem>Setting up Firebase for user authentication and schedule management.</ListItem>
          <ListItem>Integrating the Google Maps API to facilitate carpool matches based on location and routes.</ListItem>
          <ListItem>Testing the app with a small group of students for feedback and iterative improvements.</ListItem>
        </List>
      </Section>

      {/* New UI Section */}
      <Section>
        <SectionTitle>UI Design</SectionTitle>
        <Image src="/images/carpool_login.png" alt="Carpool App Login UI" />
        <Image src="/images/carpool_home.png" alt="Carpool App Home UI" />
        <UIImagesContainer>
          <HalfImage src="/images/carpool_user_data.png" alt="Carpool User Data UI" />
          <HalfImage src="/images/carpool_user_schedule.png" alt="Carpool User Schedule UI" />
        </UIImagesContainer>
        <Image src="/images/carpool_find.png" alt="Carpool App Find a Ride UI" />
        <Image src="/images/carpool_notifications.png" alt="Carpool App Notifications UI" />
      </Section>

      <Section>
        <SectionTitle>Current Status</SectionTitle>
        <Text>
          Development was paused once I confirmed that an official ACG team was already finalizing a similar carpooling platform. Instead of duplicating their work, I preserved the prototype as a demonstration of my ability to scope a problem, design matching logic, structure Firebase data models, and build a functional front‑end. The project remains a proof‑of‑concept highlighting my approach to analytical product design.
        </Text>
      </Section>

      <Button onClick={() => navigate('/projects')}>Back to Projects</Button>
    </ProjectDetailContainer>
  );
};

// Export metadata for use in the Projects page
export const carpoolingAppMetadata = {
  title: 'Carpooling App',
  description: 'Developed a React and Firebase prototype for a university carpooling system. The project was paused after learning that another official team at the university was already close to releasing a similar app.',
  tags: ['React', 'Firebase', 'Full Stack', 'Mobile'],
  languages: ['JavaScript', 'React'],
  datePublished: '2024-10-20',
  path: '/projects/carpooling-app',
  coverImage: 'images/carpool.jpg',
  status: 'Halted'
};

export default CarpoolingApp;
